import 'intersection-observer';
import React, { useCallback } from 'react';
import {
  TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import PageSection from '../client/components/page-section/PageSection';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { HeroSection, TextAlign } from '../client/types';
import { sendEvaluationLeadSubmitEventAndRedirect, sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import Hero from '../client/templates/common/hero/Hero';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { SELL_PROPERTY_BY_LOCALITY_SLUG } from '../client/graphql/queries/sellProperty';
import { New, Review, SellProperty } from '../domain/graphcms';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendEvent } from '../client/services/tracking/tracking';
import * as styles from '../client/templates/sell-property-by-locality/SellPropertyByLocality.style';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { template } from '../client/utils/string/string';
import {
  NewsSection, NewsSectionProps, GenericSection, GenericSectionProps,
  ReviewsSection,
  ReviewsSectionProps,
} from '../client/sections';
import { buildSellPropertyURLObject, relativeToAbsoluteURL } from '../client/utils/links';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import { buildNotFound } from '../client/utils/error/http/httpErrors';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { getBreadcrumbStructuredData, getStructuredData } from '../data/structured';

const HandleEvaluationLeadWizard = dynamic(
  () => import('../client/containers/handle-evaluation-lead-wizard/HandleEvaluationLeadWizard'),
  { ssr: false },
);

export interface SellPropertyByLocalityProps {
  SEO: PageHeadProps['SEO'],
  locality: string | null,
  hero: HeroSection,
  introSection: GenericSectionProps,
  districtPricesSection: GenericSectionProps,
  marketTrendSection: GenericSectionProps,
  municipalityPricesSection: GenericSectionProps,
  categoryPricesSection: GenericSectionProps,
  newsSection: NewsSectionProps | null,
  reviewsSection: ReviewsSectionProps | null,
  form: {
    title: string,
    subtitle: string,
    caption: string,
  },
}
const SellPropertyByLocality: NextPage<SellPropertyByLocalityProps> = ({
  SEO,
  locality,
  hero,
  introSection,
  districtPricesSection,
  marketTrendSection,
  municipalityPricesSection,
  categoryPricesSection,
  newsSection,
  reviewsSection,
  form,
}) => {
  const breadcrumb = React.useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Vendere casa',
      urlObject: buildSellPropertyURLObject(),
    },
    {
      label: locality,
      urlObject: buildSellPropertyURLObject(locality),
    },
  ], [locality]);

  const onStep = useCallback((step) => {
    sendEvent('step_complete', {
      category: 'form',
      action: 'submit',
      label: `hero:evaluation-lead-wizard:${step}`,
      step,
    });
  }, []);

  const genericSections = [
    introSection,
    districtPricesSection,
    marketTrendSection,
    municipalityPricesSection,
    categoryPricesSection,
  ];

  const onWizardComplete = useCallback((lead) => {
    sendEvaluationLeadSubmitEventAndRedirect('main:evaluation-lead-wizard', lead);
  }, []);

  return (
    <AppLayout activePage="sell-property">
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(
          getBreadcrumbStructuredData(breadcrumb),
        )}
      />
      <PageLayout>
        <Hero
          title={hero.title}
          description={hero.description}
          backgroundImage={hero.backgroundImage}
          primarySlot={(
            <div css={styles.heroForm}>
              <HandleEvaluationLeadWizard
                data-ref="evaluation-lead-wizard"
                sessionStorageKey="evaluation-lead-wizard"
                onStep={onStep}
                onWizardComplete={onWizardComplete}
              />
            </div>
          )}
        />
        {[
          introSection,
          districtPricesSection,
          marketTrendSection,
          municipalityPricesSection,
          categoryPricesSection,
        ].map((section, index) => {
          if (section) {
            return (
              <div key={section.id}>
                <GenericSection {...section} />
                {index < genericSections.length - 1 && <SectionDivider />}
              </div>
            );
          }
          return null;
        })}
        {reviewsSection && (
          <>
            <SectionDivider />
            <ReviewsSection {...reviewsSection} />
          </>
        )}
        {newsSection && (
          <NewsSection {...newsSection} />
        )}
        <SectionDivider />
        <PageSection data-ref="lead-form-section">
          <PageSection.Wrapper>
            <PageSection.Title
              content={form.title}
              textAlign={TextAlign.CENTER}
            />
            <PageSection.Description
              text={form.subtitle}
              textAlign={TextAlign.CENTER}
              size={TypographyName.H4}
              color="neutrals.dark"
            />
            <CreateLeadSection
              title={form.caption}
              dataRef="lead-form-main"
              onSuccess={(lead) => sendLeadSubmitEventAndRedirect('main', lead)}
              dataTracking="lead-form-main-submit"
            />
          </PageSection.Wrapper>
        </PageSection>
        <PageLayout.Wrapper>
          <PageLayout.Breadcrumb items={breadcrumb} />
        </PageLayout.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<SellPropertyByLocalityProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();

  const { data } = await graphQLClient.query<{ sellProperties: SellProperty[], news: New[], reviews: Review[] }>({
    query: SELL_PROPERTY_BY_LOCALITY_SLUG,
    variables: {
      slug: query.locality,
    },
  });

  const { sellProperties, news, reviews } = data;
  const [rawSellProperty] = sellProperties;
  assertNotNullOrUndefined(rawSellProperty, buildNotFound(`Sell property with locality slug "${query.locality}" not found`));

  const sellProperty = template(rawSellProperty, { locality: rawSellProperty?.locality?.name });
  const sellPropertyByLocalityURLObject = buildSellPropertyURLObject(rawSellProperty?.locality?.name);

  return {
    props: {
      SEO: {
        ...sellProperty?.seo,
        canonical: relativeToAbsoluteURL(sellPropertyByLocalityURLObject.as),
      },
      locality: rawSellProperty?.locality?.name,
      hero: {
        title: sellProperty.hero.title.markdown.trim(),
        description: sellProperty.hero.content?.markdown.trim() || null,
        backgroundImage: {
          SMALL: sellProperty.hero.mobileImage?.url || null,
          LARGE: sellProperty.hero.desktopImage?.url || null,
        },
      },
      introSection: {
        title: sellProperty.intro.title?.markdown || null,
        content: sellProperty.intro.content.markdown,
      },
      districtPricesSection: sellProperty.districtPrices ? {
        title: sellProperty.districtPrices.title?.markdown || null,
        content: sellProperty.districtPrices.content.markdown,
      } : null,
      marketTrendSection: sellProperty.marketTrend ? {
        title: sellProperty.marketTrend.title?.markdown || null,
        content: sellProperty.marketTrend.content.markdown,
      } : null,
      municipalityPricesSection: sellProperty.municipalityPrices ? {
        title: sellProperty.municipalityPrices.title?.markdown || null,
        content: sellProperty.municipalityPrices.content.markdown,
      } : null,
      categoryPricesSection: sellProperty.categoryPrices ? {
        title: sellProperty.categoryPrices.title?.markdown || null,
        content: sellProperty.categoryPrices.content.markdown,
      } : null,
      reviewsSection: (sellProperty.reviews && reviews.length > 0)
        ? {
          title: sellProperty.reviews?.title?.markdown?.trim() || null,
          content: sellProperty.reviews?.content?.markdown?.trim() || null,
          trustPilot: {
            label: 'Lascia una recensione',
            href: 'https://it.trustpilot.com/review/dove.it',
            dataRef: 'cta-leave-review',
            dataTracking: 'cta-leave-review',
          },
          reviews,
        }
        : null,
      newsSection: (sellProperty.news && news.length > 1)
        ? {
          title: sellProperty.news.title.markdown,
          content: sellProperty.news.content?.markdown || null,
          news: news.map((article) => ({
            id: article.id,
            title: article.title,
            alternateTitle: article.alternateTitle,
            abstract: article.abstract,
            image: {
              url: article.imageBlock?.image?.url,
              alt: article.title,
            },
          })),
        }
        : null,
      form: sellProperty.contact ? {
        title: sellProperty.contact?.title.markdown || null,
        subtitle: sellProperty.contact?.subtitle.markdown || null,
        caption: sellProperty.contact?.formTitle || null,
      } : null,
    },
  };
});

export default withError(SellPropertyByLocality);
